import React, { useState, useRef, useCallback } from 'react';
import {
    X, Plus, Trash2, Upload, FileSpreadsheet,
    ClipboardList, AlertCircle, CheckCircle2,
    ChevronRight, Loader2, Calendar, IndianRupee,
    MapPin, Briefcase, Construction, GripVertical
} from 'lucide-react';

// ── Styles ────────────────────────────────────────────────────
const MODAL_STYLES = `
  .npm-overlay {
    position: fixed; inset: 0;
    background: rgba(15,23,42,0.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; backdrop-filter: blur(6px);
    animation: npmFadeIn 0.18s ease;
    padding: 16px;
  }
  @keyframes npmFadeIn { from{opacity:0} to{opacity:1} }

  .npm-box {
    background: #fff;
    border-radius: 18px;
    box-shadow: 0 24px 64px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08);
    border: 1px solid #e2e8f0;
    width: 100%; max-width: 780px;
    max-height: 92vh; display: flex; flex-direction: column;
    animation: npmSlideUp 0.22s cubic-bezier(0.16,1,0.3,1);
  }
  @keyframes npmSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

  .npm-hdr {
    padding: 22px 26px 0;
    display: flex; justify-content: space-between; align-items: flex-start;
    flex-shrink: 0;
  }
  .npm-hdr-title { font-size: 17px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; }
  .npm-hdr-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
  .npm-close {
    background: #f1f5f9; border: none; cursor: pointer;
    padding: 6px; border-radius: 8px; display: flex;
    color: #64748b; transition: all 0.12s; flex-shrink: 0; margin-top: 2px;
  }
  .npm-close:hover { background: #e2e8f0; color: #0f172a; }

  /* Tab bar */
  .npm-tabs {
    display: flex; gap: 4px; padding: 16px 26px 0;
    border-bottom: 1px solid #f1f5f9; flex-shrink: 0;
  }
  .npm-tab {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 16px; border: none; background: transparent;
    font-size: 13px; font-weight: 600; color: #94a3b8;
    cursor: pointer; border-radius: 8px 8px 0 0;
    transition: all 0.12s; font-family: 'Plus Jakarta Sans',sans-serif;
    border-bottom: 2px solid transparent; margin-bottom: -1px;
  }
  .npm-tab:hover { color: #475569; background: #f8fafc; }
  .npm-tab.active { color: #1d4ed8; border-bottom-color: #1d4ed8; background: #eff6ff; }
  .npm-tab-badge {
    background: #1d4ed8; color: #fff; border-radius: 10px;
    font-size: 9px; font-weight: 800; padding: 1px 6px; line-height: 1.5;
  }

  /* Body */
  .npm-body { flex: 1; overflow-y: auto; padding: 22px 26px 26px; }
  .npm-body::-webkit-scrollbar { width: 4px; }
  .npm-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

  /* Form fields */
  .npm-fg { margin-bottom: 13px; }
  .npm-fg label {
    display: block; font-size: 11px; font-weight: 700;
    margin-bottom: 4px; color: #374151; text-transform: uppercase; letter-spacing: 0.4px;
  }
  .npm-fg input, .npm-fg select, .npm-fg textarea {
    width: 100%; padding: 9px 11px;
    border: 1.5px solid #e2e8f0; border-radius: 8px;
    font-size: 13px; outline: none; font-family: 'Plus Jakarta Sans',sans-serif;
    background: #fff; color: #0f172a; transition: border-color 0.12s, box-shadow 0.12s;
  }
  .npm-fg input:focus, .npm-fg select:focus, .npm-fg textarea:focus {
    border-color: #1d4ed8; box-shadow: 0 0 0 3px rgba(29,78,216,0.10);
  }
  .npm-fg .iw { position: relative; }
  .npm-fg .iw svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
  .npm-fg .iw input { padding-left: 32px; }
  .npm-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .npm-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }

  /* Section divider */
  .npm-section {
    font-size: 10px; font-weight: 700; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.8px;
    margin: 18px 0 12px; display: flex; align-items: center; gap: 8px;
  }
  .npm-section::after { content:''; flex:1; height:1px; background:#f1f5f9; }

  /* Task table */
  .npm-task-table { width: 100%; border-collapse: collapse; }
  .npm-task-table th {
    text-align: left; padding: 8px 10px; font-size: 10px;
    font-weight: 700; color: #94a3b8; background: #f8fafc;
    text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0;
  }
  .npm-task-table th:first-child { border-radius: 6px 0 0 6px; }
  .npm-task-table th:last-child  { border-radius: 0 6px 6px 0; }
  .npm-task-table td { padding: 5px 6px; border-bottom: 1px solid #f8fafc; vertical-align: middle; }
  .npm-task-table tr:last-child td { border-bottom: none; }
  .npm-task-input {
    width: 100%; padding: 7px 9px; border: 1.5px solid #e2e8f0; border-radius: 6px;
    font-size: 12px; font-family: 'Plus Jakarta Sans',sans-serif;
    background: #fff; outline: none; transition: border-color 0.12s;
  }
  .npm-task-input:focus { border-color: #1d4ed8; box-shadow: 0 0 0 2px rgba(29,78,216,0.08); }
  .npm-task-select {
    padding: 7px 9px; border: 1.5px solid #e2e8f0; border-radius: 6px;
    font-size: 11px; font-family: 'Plus Jakarta Sans',sans-serif;
    background: #fff; outline: none; cursor: pointer; width: 100%;
  }
  .npm-task-del {
    background: none; border: none; cursor: pointer; color: #cbd5e1;
    padding: 5px; border-radius: 5px; display: flex; transition: all 0.12s;
  }
  .npm-task-del:hover { background: #fee2e2; color: #ef4444; }
  .npm-add-task {
    background: none; border: none; cursor: pointer; color: #1d4ed8;
    font-weight: 700; font-size: 12px; font-family: 'Plus Jakarta Sans',sans-serif;
    display: flex; align-items: center; gap: 5px; padding: 8px 0; margin-top: 4px;
  }
  .npm-add-task:hover { color: #1e40af; }

  /* Drop zone */
  .npm-dropzone {
    border: 2px dashed #e2e8f0; border-radius: 12px;
    padding: 36px 20px; text-align: center;
    cursor: pointer; transition: all 0.15s; background: #fafbfc;
    position: relative;
  }
  .npm-dropzone:hover, .npm-dropzone.drag-over {
    border-color: #1d4ed8; background: #eff6ff;
  }
  .npm-dropzone input[type=file] {
    position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
  }
  .npm-dropzone-icon {
    width: 52px; height: 52px; border-radius: 14px; margin: 0 auto 12px;
    display: flex; align-items: center; justify-content: center;
  }
  .npm-dropzone-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
  .npm-dropzone-sub { font-size: 12px; color: #64748b; }
  .npm-dropzone-badge {
    display: inline-block; margin-top: 10px; padding: 3px 10px;
    background: #f1f5f9; border-radius: 5px; font-size: 10px;
    font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
  }

  /* Parsed preview */
  .npm-preview {
    background: #f8fafc; border: 1px solid #e2e8f0;
    border-radius: 10px; padding: 14px 16px; margin-top: 14px;
  }
  .npm-preview-title {
    font-size: 11px; font-weight: 700; color: #1d4ed8;
    text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;
    display: flex; align-items: center; gap: 6px;
  }
  .npm-preview-row {
    display: flex; justify-content: space-between;
    font-size: 12px; color: #374151; padding: 3px 0;
    border-bottom: 1px solid #f1f5f9;
  }
  .npm-preview-row:last-child { border-bottom: none; }
  .npm-preview-row span:first-child { color: #64748b; font-weight: 600; }
  .npm-preview-row span:last-child { font-weight: 700; color: #0f172a; }

  /* Task preview list */
  .npm-task-preview {
    max-height: 200px; overflow-y: auto;
    border: 1px solid #e2e8f0; border-radius: 8px; margin-top: 10px;
  }
  .npm-task-preview-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; border-bottom: 1px solid #f8fafc; font-size: 12px;
  }
  .npm-task-preview-row:last-child { border-bottom: none; }
  .npm-task-num {
    width: 20px; height: 20px; border-radius: 5px; background: #eff6ff;
    color: #1d4ed8; font-size: 10px; font-weight: 800;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* Alert */
  .npm-alert {
    display: flex; gap: 8px; align-items: flex-start;
    padding: 10px 13px; border-radius: 8px; font-size: 12px; font-weight: 500; margin-bottom: 14px;
  }
  .npm-alert.info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
  .npm-alert.warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
  .npm-alert.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }

  /* Footer */
  .npm-footer {
    padding: 16px 26px 22px; border-top: 1px solid #f1f5f9;
    display: flex; gap: 10px; justify-content: flex-end; flex-shrink: 0;
  }
  .npm-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 20px; border-radius: 8px; font-weight: 700;
    cursor: pointer; font-size: 13px; font-family: 'Plus Jakarta Sans',sans-serif;
    border: none; transition: all 0.14s; letter-spacing: -0.1px;
  }
  .npm-btn-ghost { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
  .npm-btn-ghost:hover { background: #f1f5f9; }
  .npm-btn-primary { background: #1d4ed8; color: #fff; box-shadow: 0 2px 8px rgba(29,78,216,0.25); }
  .npm-btn-primary:hover { background: #1e40af; box-shadow: 0 4px 14px rgba(29,78,216,0.35); transform: translateY(-1px); }
  .npm-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; transform: none; box-shadow: none; }

  @keyframes spin { to{transform:rotate(360deg)} }
  .npm-spin { animation: spin 0.75s linear infinite; display: inline-flex; }
`;

// ── MS Project XML Parser (browser-side, no server needed) ──────
function parseMSP(xmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');
    const get = (el, tag) => el?.getElementsByTagName(tag)?.[0]?.textContent?.trim() || '';

    const projEl = doc.getElementsByTagName('Project')[0];
    const name = get(projEl, 'Name') || get(projEl, 'Title') || '';
    const startRaw = get(projEl, 'StartDate') || get(projEl, 'Start') || '';
    const endRaw = get(projEl, 'FinishDate') || get(projEl, 'Finish') || '';

    const fmt = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const taskEls = doc.getElementsByTagName('Task');
    const tasks = [];
    for (let i = 0; i < taskEls.length; i++) {
        const t = taskEls[i];
        const taskName = get(t, 'Name');
        const outline = parseInt(get(t, 'OutlineLevel') || '1');
        const pct = parseInt(get(t, 'PercentComplete') || '0');
        const isSummary = get(t, 'Summary') === '1';
        if (!taskName || isSummary || outline > 2) continue;
        tasks.push({
            id: Date.now() + i,
            name: taskName,
            progress: pct,
            status: pct === 100 ? 'Completed' : pct > 0 ? 'In Progress' : 'Planned',
            due_date: fmt(get(t, 'Finish') || get(t, 'FinishDate')),
        });
    }
    return { name, start: fmt(startRaw), end: fmt(endRaw), tasks };
}

// ── Excel / CSV Parser ─────────────────────────────────────────
// Reads an .xlsx using SheetJS (loaded from CDN) or falls back to CSV
async function parseExcel(file) {
    // Dynamically load SheetJS if not already available
    if (!window.XLSX) {
        await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
        });
    }
    const XLSX = window.XLSX;
    const ab = await file.arrayBuffer();
    const wb = XLSX.read(ab, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

    // Try to find project info from first few rows or named columns
    let name = '', start = '', end = '', budget = '', client = '', location = '';
    const tasks = [];

    // Expected columns (case-insensitive): Task Name / Task, Progress %, Status, Due Date
    const normalize = (k) => String(k).toLowerCase().replace(/[\s_\-]+/g, '');

    rows.forEach((row, i) => {
        const keys = Object.keys(row);
        const get = (patterns) => {
            const k = keys.find(k => patterns.some(p => normalize(k).includes(p)));
            return k ? String(row[k]).trim() : '';
        };

        // First row might be project metadata
        if (i === 0) {
            name = get(['projectname', 'project', 'name', 'sitename']) || name;
            client = get(['client', 'clientname', 'owner']) || client;
            location = get(['location', 'site', 'address']) || location;
            budget = get(['budget', 'value', 'cost']) || budget;
            start = get(['start', 'startdate', 'commencementdate']) || start;
            end = get(['end', 'finish', 'enddate', 'completiondate']) || end;
        }

        // Every row with a task name becomes a task
        const taskName = get(['taskname', 'task', 'activity', 'description', 'work', 'milestone']);
        if (taskName && taskName.length > 1 && taskName.toLowerCase() !== 'task name') {
            const pct = parseInt(get(['progress', 'percentcomplete', 'complete', 'pct', '%complete'])) || 0;
            const status = get(['status', 'state']) ||
                (pct === 100 ? 'Completed' : pct > 0 ? 'In Progress' : 'Planned');
            const due = get(['due', 'duedate', 'finish', 'enddate', 'targetdate']);
            tasks.push({
                id: Date.now() + i,
                name: taskName,
                progress: Math.min(100, Math.max(0, pct)),
                status: status.charAt(0).toUpperCase() + status.slice(1),
                due_date: due,
            });
        }
    });

    return { name, client, location, budget, start, end, tasks };
}

// ── Main Component ────────────────────────────────────────────
export default function NewProjectModal({ onClose, onSave, projects }) {
    const [tab, setTab] = useState('manual');   // 'manual' | 'msp' | 'excel'
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [tasks, setTasks] = useState([]);
    const [parsed, setParsed] = useState(null);       // parsed file data
    const [fileState, setFileState] = useState('idle');     // 'idle'|'parsing'|'done'|'error'
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef();

    // Manual form state
    const [form, setForm] = useState({
        name: '', client: '', location: '', budget: '', startDate: '', endDate: '',
    });
    const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // ── Task helpers ────────────────────────────────────────────
    const addTask = () => setTasks(p => [...p, {
        id: Date.now(), name: '', progress: 0, status: 'Planned', due_date: '',
    }]);
    const updTask = (id, k, v) => setTasks(p =>
        p.map(t => t.id === id ? { ...t, [k]: k === 'progress' ? Math.min(100, Math.max(0, parseInt(v) || 0)) : v } : t)
    );
    const delTask = (id) => setTasks(p => p.filter(t => t.id !== id));

    // ── File parsing ────────────────────────────────────────────
    const handleFile = useCallback(async (file) => {
        if (!file) return;
        setFileState('parsing');
        setError('');
        setParsed(null);
        setTasks([]);
        try {
            let result;
            const ext = file.name.split('.').pop().toLowerCase();

            if (ext === 'xml') {
                // Parse XML in browser — instant, no server needed
                const text = await file.text();
                result = parseMSP(text);

            } else if (['mpp', 'mpt', 'mpx'].includes(ext)) {
                // Upload to your NAS server — Java + MPXJ installed at /volume1/@appstore/
                const formData = new FormData();
                formData.append('file', file, file.name);
                const res = await fetch('https://api.themrconstruction.com/mpp', {
                    method: 'POST',
                    body: formData,
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.error || `Server error ${res.status}`);
                }
                const data = await res.json();
                result = {
                    name: data.project?.name || file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
                    start: data.project?.start || '',
                    end: data.project?.end || '',
                    tasks: data.tasks || [],
                };

            } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
                result = await parseExcel(file);
            } else {
                throw new Error('Unsupported file. Upload .mpp/.mpt/.mpx for MS Project, or .xlsx/.csv for Excel.');
            }

            setParsed(result);
            setTasks(result.tasks || []);
            if (result.name) setF('name', result.name);
            if (result.client) setF('client', result.client);
            if (result.location) setF('location', result.location);
            if (result.budget) setF('budget', (result.budget || '').replace(/[^\d.]/g, ''));
            if (result.start) setF('startDate', result.start);
            if (result.end) setF('endDate', result.end);
            setFileState('done');
        } catch (err) {
            setFileState('error');
            setError(err.message || 'Failed to parse file.');
        }
    }, []);

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    // ── Save ────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!form.name.trim()) { setError('Project name is required.'); return; }
        setSaving(true);
        setError('');
        try {
            const validTasks = tasks.filter(t => t.name.trim());
            await onSave({
                name: form.name.trim(),
                client: form.client.trim(),
                location: form.location.trim(),
                budget: form.budget ? form.budget + 'L' : '0L',
                startDate: form.startDate,
                endDate: form.endDate,
                tasks: validTasks,
            });
        } catch (err) {
            setError(err.message || 'Failed to save project.');
            setSaving(false);
        }
    };

    const taskCount = tasks.filter(t => t.name.trim()).length;

    return (
        <>
            <style>{MODAL_STYLES}</style>
            <div className="npm-overlay" onClick={(e) => e.target.classList.contains('npm-overlay') && onClose()}>
                <div className="npm-box">

                    {/* Header */}
                    <div className="npm-hdr">
                        <div>
                            <div className="npm-hdr-title">Register New Project</div>
                            <div className="npm-hdr-sub">Add project details manually or import from MS Project / Excel</div>
                        </div>
                        <button className="npm-close" onClick={onClose}><X size={16} /></button>
                    </div>

                    {/* Tabs */}
                    <div className="npm-tabs">
                        <button className={`npm-tab ${tab === 'manual' ? 'active' : ''}`} onClick={() => setTab('manual')}>
                            <ClipboardList size={14} /> Manual Entry
                        </button>

                        <button className={`npm-tab ${tab === 'msp' ? 'active' : ''}`} onClick={() => setTab('msp')}>
                            <Upload size={14} /> MS Project (.mpp) (.mpp)
                        </button>

                        <button className={`npm-tab ${tab === 'excel' ? 'active' : ''}`} onClick={() => setTab('excel')}>
                            <FileSpreadsheet size={14} /> Excel / CSV
                        </button>
                    </div>

                    {/* Body */}
                    <div className="npm-body">
                        {error && (
                            <div className="npm-alert warn" style={{ marginBottom: '14px' }}>
                                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                {error}
                            </div>
                        )}

                        {/* ── MANUAL TAB ─────────────────────────────── */}
                        {tab === 'manual' && (
                            <>
                                <div className="npm-section">Project Details</div>
                                <div className="npm-fg">
                                    <label>Project Name *</label>
                                    <div className="iw">
                                        <Construction size={14} />
                                        <input value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Project name" required />
                                    </div>
                                </div>
                                <div className="npm-grid2">
                                    <div className="npm-fg">
                                        <label>Client / Owner</label>
                                        <div className="iw"><Briefcase size={14} />
                                            <input value={form.client} onChange={e => setF('client', e.target.value)} placeholder="Client name" />
                                        </div>
                                    </div>
                                    <div className="npm-fg">
                                        <label>Location</label>
                                        <div className="iw"><MapPin size={14} />
                                            <input value={form.location} onChange={e => setF('location', e.target.value)} placeholder="Site location" />
                                        </div>
                                    </div>
                                </div>
                                <div className="npm-grid3">
                                    <div className="npm-fg">
                                        <label>Budget (Lakhs)</label>
                                        <div className="iw"><IndianRupee size={14} />
                                            <input type="number" value={form.budget} onChange={e => setF('budget', e.target.value)} placeholder="0" />
                                        </div>
                                    </div>
                                    <div className="npm-fg">
                                        <label>Start Date</label>
                                        <div className="iw"><Calendar size={14} />
                                            <input value={form.startDate} onChange={e => setF('startDate', e.target.value)} placeholder="e.g. Jan 2025" />
                                        </div>
                                    </div>
                                    <div className="npm-fg">
                                        <label>End Date</label>
                                        <div className="iw"><Calendar size={14} />
                                            <input value={form.endDate} onChange={e => setF('endDate', e.target.value)} placeholder="e.g. Dec 2025" />
                                        </div>
                                    </div>
                                </div>

                                <div className="npm-section">
                                    Tasks / Schedule
                                    {taskCount > 0 && <span style={{ background: '#1d4ed8', color: '#fff', borderRadius: '10px', fontSize: '10px', fontWeight: '800', padding: '1px 8px' }}>{taskCount}</span>}
                                </div>

                                {tasks.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '24px', border: '1px dashed #e2e8f0', borderRadius: '10px', color: '#94a3b8', fontSize: '13px' }}>
                                        No tasks yet. Click below to add your first task.
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="npm-task-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '38%' }}>Task Name</th>
                                                    <th style={{ width: '12%' }}>Progress %</th>
                                                    <th style={{ width: '16%' }}>Status</th>
                                                    <th style={{ width: '20%' }}>Due Date</th>
                                                    <th style={{ width: '8%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tasks.map((t, idx) => (
                                                    <tr key={t.id}>
                                                        <td>
                                                            <input className="npm-task-input" value={t.name}
                                                                onChange={e => updTask(t.id, 'name', e.target.value)}
                                                                placeholder={`Task ${idx + 1}`} />
                                                        </td>
                                                        <td>
                                                            <input className="npm-task-input" type="number" min="0" max="100"
                                                                value={t.progress} onChange={e => updTask(t.id, 'progress', e.target.value)}
                                                                style={{ textAlign: 'center' }} />
                                                        </td>
                                                        <td>
                                                            <select className="npm-task-select" value={t.status}
                                                                onChange={e => updTask(t.id, 'status', e.target.value)}>
                                                                <option>Planned</option>
                                                                <option>In Progress</option>
                                                                <option>Completed</option>
                                                                <option>Delayed</option>
                                                                <option>Blocked</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input className="npm-task-input" value={t.due_date || ''}
                                                                onChange={e => updTask(t.id, 'due_date', e.target.value)}
                                                                placeholder="e.g. Jun 2025" />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button className="npm-task-del" onClick={() => delTask(t.id)}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <button className="npm-add-task" onClick={addTask}>
                                    <Plus size={14} /> Add Task
                                </button>
                            </>
                        )}

                        {/* ── MS PROJECT TAB ──────────────────────────── */}
                        {tab === 'msp' && (
                            <>
                                <div className="npm-alert info">
                                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                    Upload your MS Project file (<strong>.mpp</strong>, <strong>.mpt</strong>, or <strong>.mpx</strong>). All tasks, dates and progress are extracted automatically.
                                </div>

                                <div
                                    className={`npm-dropzone ${dragOver ? 'drag-over' : ''}`}
                                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={onDrop}
                                >
                                    <input type="file" accept=".mpp,.mpt,.mpx" ref={fileRef}
                                        onChange={e => handleFile(e.target.files?.[0])} />
                                    <div className="npm-dropzone-icon" style={{ background: '#eff6ff' }}>
                                        <Upload size={26} color="#1d4ed8" />
                                    </div>
                                    {fileState === 'parsing'
                                        ? <><div className="npm-dropzone-title">Uploading &amp; parsing…</div><div className="npm-dropzone-sub">Server is reading your MS Project file</div></>
                                        : fileState === 'done'
                                            ? <div className="npm-dropzone-title" style={{ color: '#16a34a' }}>✓ MS Project file parsed successfully</div>
                                            : <div className="npm-dropzone-title">Drop MS Project file here</div>
                                    }
                                    {fileState !== 'done' && <div className="npm-dropzone-sub">or click to browse</div>}
                                    <span className="npm-dropzone-badge">.mpp · .mpt · .mpx</span>
                                </div>

                                {fileState === 'done' && parsed && (
                                    <>
                                        <div className="npm-preview">
                                            <div className="npm-preview-title"><CheckCircle2 size={13} /> Extracted from MS Project</div>
                                            {parsed.name && <div className="npm-preview-row"><span>Project Name</span><span>{parsed.name}</span></div>}
                                            {parsed.start && <div className="npm-preview-row"><span>Start Date</span><span>{parsed.start}</span></div>}
                                            {parsed.end && <div className="npm-preview-row"><span>End Date</span><span>{parsed.end}</span></div>}
                                            <div className="npm-preview-row"><span>Tasks Found</span><span>{tasks.length}</span></div>
                                        </div>

                                        <div className="npm-section" style={{ marginTop: '16px' }}>Review / Edit Details</div>
                                        <div className="npm-fg">
                                            <label>Project Name *</label>
                                            <input value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Project name"
                                                style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                        </div>
                                        <div className="npm-grid2">
                                            <div className="npm-fg">
                                                <label>Client / Owner</label>
                                                <input value={form.client} onChange={e => setF('client', e.target.value)} placeholder="Client name"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                            <div className="npm-fg">
                                                <label>Location</label>
                                                <input value={form.location} onChange={e => setF('location', e.target.value)} placeholder="Location"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                        </div>
                                        <div className="npm-grid2">
                                            <div className="npm-fg">
                                                <label>Budget (Lakhs)</label>
                                                <input type="number" value={form.budget} onChange={e => setF('budget', e.target.value)} placeholder="0"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                            <div className="npm-fg">
                                                <label>End Date</label>
                                                <input value={form.endDate} onChange={e => setF('endDate', e.target.value)} placeholder="e.g. Dec 2025"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                        </div>

                                        {tasks.length > 0 && (
                                            <>
                                                <div className="npm-section">Imported Tasks ({tasks.length})</div>
                                                <div className="npm-task-preview">
                                                    {tasks.map((t, i) => (
                                                        <div key={t.id} className="npm-task-preview-row">
                                                            <span className="npm-task-num">{i + 1}</span>
                                                            <span style={{ flex: 1, fontWeight: 600 }}>{t.name}</span>
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>{t.progress}%</span>
                                                            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: t.status === 'Completed' ? '#d1fae5' : t.status === 'In Progress' ? '#fef3c7' : '#f1f5f9', color: t.status === 'Completed' ? '#065f46' : t.status === 'In Progress' ? '#92400e' : '#64748b', fontWeight: 700, marginLeft: '6px' }}>{t.status}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* ── EXCEL TAB ──────────────────────────────── */}
                        {tab === 'excel' && (
                            <>
                                <div className="npm-alert info">
                                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                    Upload an Excel or CSV file. Include columns: <strong>Task Name, Progress %, Status, Due Date</strong>. First row can include project info (Name, Client, Location, Budget).
                                </div>

                                <div
                                    className={`npm-dropzone ${dragOver ? 'drag-over' : ''}`}
                                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={onDrop}
                                >
                                    <input type="file" accept=".xlsx,.xls,.csv" ref={fileRef}
                                        onChange={e => handleFile(e.target.files?.[0])} />
                                    <div className="npm-dropzone-icon" style={{ background: '#f0fdf4' }}>
                                        <FileSpreadsheet size={26} color="#16a34a" />
                                    </div>
                                    {fileState === 'parsing'
                                        ? <><div className="npm-dropzone-title">Reading spreadsheet…</div><div className="npm-dropzone-sub">Loading SheetJS library…</div></>
                                        : fileState === 'done'
                                            ? <div className="npm-dropzone-title" style={{ color: '#16a34a' }}>✓ Spreadsheet parsed successfully</div>
                                            : <div className="npm-dropzone-title">Drop Excel or CSV here</div>
                                    }
                                    {fileState !== 'done' && <div className="npm-dropzone-sub">or click to browse</div>}
                                    <span className="npm-dropzone-badge">.xlsx · .xls · .csv</span>
                                </div>

                                {fileState === 'done' && parsed && (
                                    <>
                                        <div className="npm-preview">
                                            <div className="npm-preview-title"><CheckCircle2 size={13} /> Extracted Data</div>
                                            {parsed.name && <div className="npm-preview-row"><span>Project Name</span><span>{parsed.name}</span></div>}
                                            {parsed.client && <div className="npm-preview-row"><span>Client</span><span>{parsed.client}</span></div>}
                                            {parsed.budget && <div className="npm-preview-row"><span>Budget</span><span>₹{parsed.budget}L</span></div>}
                                            <div className="npm-preview-row"><span>Tasks Found</span><span>{tasks.length}</span></div>
                                        </div>

                                        <div className="npm-section" style={{ marginTop: '16px' }}>Review / Edit Details</div>
                                        <div className="npm-fg">
                                            <label>Project Name *</label>
                                            <input value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Project name"
                                                style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                        </div>
                                        <div className="npm-grid2">
                                            <div className="npm-fg">
                                                <label>Client</label>
                                                <input value={form.client} onChange={e => setF('client', e.target.value)} placeholder="Client"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                            <div className="npm-fg">
                                                <label>Location</label>
                                                <input value={form.location} onChange={e => setF('location', e.target.value)} placeholder="Location"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                        </div>
                                        <div className="npm-grid2">
                                            <div className="npm-fg">
                                                <label>Budget (Lakhs)</label>
                                                <input type="number" value={form.budget} onChange={e => setF('budget', e.target.value)} placeholder="0"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                            <div className="npm-fg">
                                                <label>Start Date</label>
                                                <input value={form.startDate} onChange={e => setF('startDate', e.target.value)} placeholder="e.g. Jan 2025"
                                                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif', outline: 'none' }} />
                                            </div>
                                        </div>

                                        {tasks.length > 0 && (
                                            <>
                                                <div className="npm-section">Imported Tasks ({tasks.length}) — editable below</div>
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table className="npm-task-table">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '40%' }}>Task Name</th>
                                                                <th style={{ width: '14%' }}>Progress %</th>
                                                                <th style={{ width: '18%' }}>Status</th>
                                                                <th style={{ width: '20%' }}>Due Date</th>
                                                                <th style={{ width: '8%' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {tasks.map((t, idx) => (
                                                                <tr key={t.id}>
                                                                    <td><input className="npm-task-input" value={t.name} onChange={e => updTask(t.id, 'name', e.target.value)} /></td>
                                                                    <td><input className="npm-task-input" type="number" min="0" max="100" value={t.progress} onChange={e => updTask(t.id, 'progress', e.target.value)} style={{ textAlign: 'center' }} /></td>
                                                                    <td>
                                                                        <select className="npm-task-select" value={t.status} onChange={e => updTask(t.id, 'status', e.target.value)}>
                                                                            <option>Planned</option><option>In Progress</option><option>Completed</option><option>Delayed</option><option>Blocked</option>
                                                                        </select>
                                                                    </td>
                                                                    <td><input className="npm-task-input" value={t.due_date || ''} onChange={e => updTask(t.id, 'due_date', e.target.value)} placeholder="Due date" /></td>
                                                                    <td style={{ textAlign: 'center' }}><button className="npm-task-del" onClick={() => delTask(t.id)}><Trash2 size={13} /></button></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <button className="npm-add-task" onClick={addTask}><Plus size={14} /> Add Task</button>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="npm-footer">
                        <button className="npm-btn npm-btn-ghost" onClick={onClose}>Cancel</button>
                        <button
                            className="npm-btn npm-btn-primary"
                            onClick={handleSave}
                            disabled={saving || !form.name.trim()}
                        >
                            {saving
                                ? <><span className="npm-spin"><Loader2 size={14} /></span> Saving…</>
                                : <><Construction size={14} /> Create Project {taskCount > 0 ? `with ${taskCount} Task${taskCount > 1 ? 's' : ''}` : ''}</>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}